/* ---------------------------------------------------------------------------
   Header de 2 filas — panel compartido del nav desktop.

   El panel es uno solo: cambiar de ítem intercambia el grupo de columnas y deja
   fijos los listados de píldoras del pie.

   También clona los íconos de columna que aportan los bloques "Ítem del menú".
   Van por <template> + script, y no impresos en su lugar, porque Shopify no le
   deja al bloque padre leer los settings de sus hijos: cada hijo se renderiza
   solo y no sabe dónde va. Es el mismo patrón de los enlaces de utilidad del
   topbar.
--------------------------------------------------------------------------- */
(() => {
  const CLOSE_DELAY = 120;
  const CLOSE_ANIMATION = 170; // igual a `ironside-inline-panel-out` en el CSS

  function paintIcons(nav) {
    nav.querySelectorAll('[data-ironside-nav-icons]').forEach((template) => {
      const key = template.getAttribute('data-ironside-nav-icons');
      const columns = nav.querySelector(
        `[data-ironside-inline-columns="${CSS.escape(key)}"], [data-ironside-inline-columns][data-nav-key="${CSS.escape(key)}"]`
      );
      if (!columns) return;

      template.content.querySelectorAll('[data-col]').forEach((source) => {
        const icon = source.firstElementChild;
        if (!icon) return;
        const slot = columns.querySelector(
          `.ironside-inline-column__icon-slot[data-col="${source.getAttribute('data-col')}"]`
        );
        if (slot) slot.replaceChildren(icon.cloneNode(true));
      });
    });
  }

  // El panel va a todo el ancho de la ventana, pero está dentro del nav: hay que
  // correrlo hacia la izquierda tanto como el nav esté separado del borde, y
  // hacia abajo hasta el borde de la fila (el nav es más bajo que la fila y
  // queda centrado en ella).
  function measure(nav) {
    const rect = nav.getBoundingClientRect();
    const row = nav.closest('.header__row');
    const gap = row ? Math.max(0, row.getBoundingClientRect().bottom - rect.bottom) : 0;
    nav.style.setProperty('--inline-panel-left', `${-rect.left}px`);
    nav.style.setProperty('--inline-panel-top', `${gap}px`);
    // Un menú con muchas columnas puede ser más alto que la ventana: el panel
    // se corta en el borde de abajo y scrollea por dentro.
    const available = Math.floor(window.innerHeight - (rect.bottom + gap));
    nav.style.setProperty('--inline-panel-max-height', `${Math.max(available, 200)}px`);
  }

  function init(nav) {
    if (nav.dataset.ironsideInlineNavReady === 'true') return;
    nav.dataset.ironsideInlineNavReady = 'true';

    const panel = nav.querySelector('[data-ironside-inline-panel]');
    const triggers = Array.from(nav.querySelectorAll('[data-ironside-inline-trigger]'));
    if (!panel || triggers.length === 0) return;

    paintIcons(nav);
    measure(nav);
    window.addEventListener('resize', () => measure(nav));
    // Sin header sticky, el nav se mueve al scrollear y cambia el alto libre.
    window.addEventListener('scroll', () => { if (!panel.hidden) measure(nav); }, { passive: true });

    const groups = Array.from(nav.querySelectorAll('[data-ironside-inline-columns]'));
    let closeTimer = null;
    let openKey = null;

    const cancelClose = () => {
      if (closeTimer === null) return;
      clearTimeout(closeTimer);
      closeTimer = null;
    };

    // El panel se esconde recién cuando termina la animación de salida (ver el
    // CSS). Si se vuelve a abrir en el medio, se cancela y queda visible.
    let hideTimer = null;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const cancelHide = () => {
      if (hideTimer === null) return;
      clearTimeout(hideTimer);
      hideTimer = null;
      panel.classList.remove('is-closing');
    };

    const close = () => {
      cancelClose();
      if (openKey === null) return;
      triggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
      openKey = null;

      const hide = () => {
        hideTimer = null;
        panel.classList.remove('is-closing');
        panel.hidden = true;
        groups.forEach((group) => { group.hidden = true; });
      };

      if (reducedMotion.matches) {
        hide();
        return;
      }
      panel.classList.add('is-closing');
      hideTimer = setTimeout(hide, CLOSE_ANIMATION);
    };

    const open = (key) => {
      cancelClose();
      cancelHide();
      if (openKey === key) return;

      const target = groups.find((group) => group.getAttribute('data-ironside-inline-columns') === key);
      if (!target) return;

      measure(nav);

      groups.forEach((group) => { group.hidden = group !== target; });
      triggers.forEach((trigger) => {
        trigger.setAttribute(
          'aria-expanded',
          trigger.getAttribute('data-ironside-inline-trigger') === key ? 'true' : 'false'
        );
      });
      panel.hidden = false;
      openKey = key;
    };

    const scheduleClose = () => {
      cancelClose();
      closeTimer = setTimeout(close, CLOSE_DELAY);
    };

    triggers.forEach((trigger) => {
      const key = trigger.getAttribute('data-ironside-inline-trigger');
      const host = trigger.closest('.ironside-inline-nav__item') || trigger;

      host.addEventListener('mouseenter', () => open(key));
      host.addEventListener('mouseleave', scheduleClose);

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        if (openKey === key) close();
        else open(key);
      });

      trigger.addEventListener('focus', () => open(key));
    });

    panel.addEventListener('mouseenter', cancelClose);
    panel.addEventListener('mouseleave', scheduleClose);

    nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || openKey === null) return;
      const active = triggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
      close();
      if (active) active.focus();
    });

    // Tab fuera del nav cierra: el panel abierto tapa el contenido de la página.
    nav.addEventListener('focusout', (event) => {
      if (nav.contains(event.relatedTarget)) return;
      close();
    });

    document.addEventListener('click', (event) => {
      if (nav.contains(event.target)) return;
      close();
    });
  }

  const initAll = (root) => {
    (root || document).querySelectorAll('[data-ironside-inline-nav]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // El editor de temas vuelve a renderizar la sección al cambiar un setting.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
